import { Box, Button, Grid, Link, Typography } from '@mui/material';
import { useNavigate, useSearchParams } from 'react-router-dom';
import { useState } from 'react';
import { useGoogleReCaptcha } from 'react-google-recaptcha-v3';
import TextField from '@/components/textField';
import ErrorMessage from '@/components/errorMessage';
import { useApiContext } from '@/contexts/apiContext';
import { isEmail } from 'validator';
import { validatePassword } from '@/utils';

const ResetPasswordPage = () => {
  const apiContext = useApiContext();
  const navigate = useNavigate();
  const { executeRecaptcha } = useGoogleReCaptcha();

  const [searchParams] = useSearchParams();
  const resetToken = searchParams.get('resetToken');

  const [loading, setLoading] = useState(false);
  const [email, setEmail] = useState('');
  const [password, setPassword] = useState('');
  const [confirmPassword, setConfirmPassword] = useState('');
  const [errors, setErrors] = useState<any>({});
  const [apiError, setApiError] = useState<any>();
  const [submitted, setSubmitted] = useState(false);

  const handleSubmit = async () => {
    setApiError(null);
    const errors: any = {};
    let hasError = false;

    if (!executeRecaptcha) {
      setApiError('ReCAPTCHA not ready. Please try again.');
      return;
    }

    try {
      if (!email) {
        errors['email'] = 'Required';
        hasError = true;
      }
      if (!isEmail(email)) {
        errors['email'] = 'Please enter valid email';
        hasError = true;
      }
      if (!password) {
        errors['password'] = 'Required';
        hasError = true;
      }
      if (!validatePassword(password)) {
        errors['password'] = 'Should be 8 to 100 chars long';
        hasError = true;
      }
      if (!confirmPassword) {
        errors['confirmPassword'] = 'Required';
        hasError = true;
      }
      if (password !== confirmPassword) {
        errors['confirmPassword'] = 'Passwords must be same';
        hasError = true;
      }
      if (!hasError) {
        setLoading(true);
        const recaptchaToken = await executeRecaptcha('reset_password');
        await apiContext.resetPassword({ resetToken, email, password, recaptchaToken });
        setSubmitted(true);
      }
    } catch (error: any) {
      setApiError(error);
    }
    setErrors(errors);
    setLoading(false);
  };

  const handleCancel = () => navigate(submitted ? '/login' : '/');

  return (
    <Grid container justifyContent="center" alignItems={'flex-start'} spacing={4}>
      <Grid size={{ xs: 12, md: 6 }}>
        <Box
          sx={{
            border: 1,
            borderColor: 'divider',
            borderRadius: '12px',
            overflow: 'hidden',
            bgcolor: 'background.paper',
          }}
        >
          {/* Header */}
          <Box sx={{ px: 3, py: 2, borderBottom: 1, borderColor: 'divider' }}>
            <Typography variant="h6" sx={{ fontWeight: 700 }}>
              Change password
            </Typography>
            <Typography variant="caption" color="text.secondary">
              Enter your email and choose a new password
            </Typography>
            {apiError && (
              <Box sx={{ mt: 1 }}>
                <ErrorMessage error={apiError} />
              </Box>
            )}
          </Box>

          {/* Content */}
          <Box sx={{ px: 3, py: 2.5 }}>
            {!submitted && (
              <Grid container spacing={1.5}>
                <Grid size={12}>
                  <TextField
                    value={email}
                    label={'Email Address'}
                    required
                    error={errors['email']}
                    onChange={setEmail}
                    type="email"
                    maxLength={100}
                  />
                </Grid>
                <Grid size={{ xs: 12, md: 6 }}>
                  <TextField
                    value={password}
                    label={'Password'}
                    required
                    error={errors['password']}
                    onChange={setPassword}
                    type="password"
                    maxLength={100}
                  />
                </Grid>
                <Grid size={{ xs: 12, md: 6 }}>
                  <TextField
                    value={confirmPassword}
                    label={'Confirm Password'}
                    required
                    error={errors['confirmPassword']}
                    onChange={setConfirmPassword}
                    type="password"
                    maxLength={100}
                  />
                </Grid>
                <Grid size={12}>
                  <Typography variant="caption" color="text.secondary">
                    This site is protected by reCAPTCHA and the Google{' '}
                    <Link href="https://policies.google.com/privacy" target="_blank">
                      Privacy Policy
                    </Link>{' '}
                    and{' '}
                    <Link href="https://policies.google.com/terms" target="_blank">
                      Terms of Service
                    </Link>{' '}
                    apply.
                  </Typography>
                </Grid>
              </Grid>
            )}
            {submitted && (
              <Typography variant="body2" color="text.secondary">
                Your password has been reset successfully.
              </Typography>
            )}
          </Box>

          {/* Action bar */}
          <Box
            sx={{
              px: 3,
              py: 1.5,
              borderTop: 1,
              borderColor: 'divider',
              display: 'flex',
              justifyContent: 'flex-end',
              gap: 1,
            }}
          >
            <Button variant="text" size="small" onClick={handleCancel} sx={{ textTransform: 'none', fontWeight: 600 }}>
              {submitted ? 'Go to Login' : 'Cancel'}
            </Button>
            {!submitted && email && (
              <Button
                loading={loading}
                size="small"
                color="secondary"
                variant="contained"
                onClick={handleSubmit}
                sx={{
                  textTransform: 'none',
                  fontWeight: 700,
                  borderRadius: '8px',
                  boxShadow: 'none',
                  '&:hover': { boxShadow: 'none' },
                  color: 'text.primary',
                }}
              >
                Submit
              </Button>
            )}
          </Box>
        </Box>
      </Grid>
    </Grid>
  );
};

export default ResetPasswordPage;
